
// Utils

import { cloneDeep, differenceWith, isEqual } from '../miscellaneous/objectHelpers';
import { cloneObject } from '../miscellaneous/cloneObject';
import { getCurrentAndMaxAchievedPages } from '@utils/miscellaneous/getCurrentAndMaxAchievedPages';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { maskValue } from '../miscellaneous/mask.utils';
import { processJsonTools } from '@utils/tools/processJsonTools';
import { processUiProperties } from '@utils/tools/processUiProperties';
import { questionsVisibility } from '../miscellaneous/pageAndQuestionsVisibility';
import { socialSecurityNumberTools } from '../tools/socialSecurityNumberTools';
import { updateDirtyFlags } from '@utils/miscellaneous/updateDirtyFlags';
import { updateImiValues, updateSsnValues } from '@utils/householdMember/updateMaskedValues';

// Constants

import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { PAGES } from '@constants/ssap/flows/pagesTypes';

// Types

import { GenderIdentification, SexualOrientation } from '@components/ssap/SsapTypes';

export const processInPipeline = (...fns) => (...args) => {
    const [ params, defaultReturnValue ] = args;

    return fns.reduce(
        (acc, fn) => {
            return fn(
                {
                    ...params,
                    dataAfterChanges: cloneDeep(acc.cleanedData)
                },
                acc
            );
        },
        defaultReturnValue
    );
};

const performCleanUpAndChecksForEthnicityAndRace = (...args) => {
    const [ params, defaultReturnValue ] = args;

    const {
        dataAfterChanges,
        dataBeforeChanges,
        isSogiFeatureEnabled,
        serverDate
    } = params;

    // We are getting arrays of booleans for each household member
    // "true" if we are going to render SOGI questions for this HHM on ETHNICITY_AND_RACE_PAGE
    // "false" if we are not
    const [
        hhmVisibilityBeforeChanges,
        hhmVisibilityAfterChanges
    ] = [ dataBeforeChanges, dataAfterChanges ].map(data => {
        return data.taxHousehold.householdMember.map(hhm => {
            return questionsVisibility.isSogiShown({
                hhm,
                isSogiFeatureEnabled,
                serverDate
            });
        });
    });

    const areChanges = !isEqual(hhmVisibilityBeforeChanges, hhmVisibilityAfterChanges);

    let cleanedData = defaultReturnValue.cleanedData,
        shouldRestrictNavigation = defaultReturnValue.shouldRestrictNavigation;

    if(areChanges) {
        hhmVisibilityAfterChanges.forEach((isVisible, index) => {
            if(isVisible === false) {
                // Since HHM won't be rendered on ETHNICITY_AND_RACE_PAGE
                // then we need to clean up all the data related to this HHM
                cleanedData.taxHousehold.householdMember[index] = {
                    ...cleanedData.taxHousehold.householdMember[index],
                    genderIdentification: GenderIdentification.UNSPECIFIED,
                    sexualOrientation: SexualOrientation.UNSPECIFIED
                };
            } else {
                // We do nothing here
            }
        });
    }

    // We are not restricting navigation because Date of Birth input is non-standalone one
    // Restriction will be made by input
    return {
        cleanedData,
        shouldRestrictNavigation
    };
};

const performCleanUpAndChecksForMedicaidTermination = (...args) => {
    const [ params, defaultReturnValue ] = args;

    const {
        dataAfterChanges,
        dataBeforeChanges,
        medicaidTerminationFeatureEnabled,
        serverDate,
        medicaidEndDateOverride
    } = params;

    // We are getting arrays of booleans for each household member
    // "true" if we are going to render questions for this HHM on MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
    // "false" if we are not
    const [
        hhmVisibilityBeforeChanges,
        hhmVisibilityAfterChanges
    ] = [ dataBeforeChanges, dataAfterChanges ].map(data => {
        return data.taxHousehold.householdMember.map(hhm => {
            return questionsVisibility.isMedicaidChipDenialShown({
                hhm,
                medicaidTerminationFeatureEnabled,
                serverDate,
                medicaidEndDateOverride
            });
        });
    });

    const areChanges = !isEqual(hhmVisibilityBeforeChanges, hhmVisibilityAfterChanges);

    let cleanedData = defaultReturnValue.cleanedData,
        shouldRestrictNavigation = defaultReturnValue.shouldRestrictNavigation;

    if(areChanges) {
        hhmVisibilityAfterChanges.forEach((isVisible, index) => {
            if(isVisible === false) {
                // Since HHM won't be rendered on MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
                // then we need to clean up all the data related to this HHM
                cleanedData.taxHousehold.householdMember[index] = {
                    ...cleanedData.taxHousehold.householdMember[index],
                    ...cloneObject(EMPTY_OBJECTS.MEDICAID_CHIP_DENIAL)
                };
            } else {
                // If we track that HHM will be rendered on MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
                // but in the data before changes this HHM was not rendered
                // then we need to restrict navigation and set maxAchievedPage to MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
                if(shouldRestrictNavigation === false && hhmVisibilityBeforeChanges[index] === false) {
                    shouldRestrictNavigation = true;
                }
            }
        });
    }

    return {
        cleanedData,
        shouldRestrictNavigation
    };
};


const performHomeAddressSyncForHhmsWhoLiveTogetherWithPcp = (...args) => {
    const [ _, defaultReturnValue ] = args;

    let cleanedData = defaultReturnValue.cleanedData,
        shouldRestrictNavigation = defaultReturnValue.shouldRestrictNavigation;

    const hhms = cleanedData.taxHousehold.householdMember;
    const pcpHomeAddress = householdMemberTools.getPrimaryContact(hhms).householdContact.homeAddress;

    householdMemberTools.updatePrimaryContact(
        hhms,
        {
            householdContact: {
                homeAddress: pcpHomeAddress
            }
        },
        false,
        [
            {
                checkValues: [ false, null ],
                checkKey: 'livesAtOtherAddressIndicator',
                syncKey: 'householdContact.homeAddress'
            }
        ]
    );

    return {
        cleanedData,
        shouldRestrictNavigation
    };
};

export const performCleanUpAndChecks = params => {

    const {
        dataAfterChanges,
        pageName,
    } = params;

    const defaultReturnValue = {
        cleanedData: cloneDeep(dataAfterChanges),
        shouldRestrictNavigation: false
    };

    const payload = [ params, defaultReturnValue ];

    switch(pageName) {
        case PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE:
            return performCleanUpAndChecksForEthnicityAndRace(...payload);
        case PAGES.PRIMARY_CONTACT_INFORMATION_PAGE:
            return processInPipeline(
                performHomeAddressSyncForHhmsWhoLiveTogetherWithPcp,
                performCleanUpAndChecksForEthnicityAndRace
            )(...payload);
        case PAGES.MEDICAID_TERMINATION_DETAILS_PAGE:
            return performCleanUpAndChecksForMedicaidTermination(...payload);
        default:
            return defaultReturnValue;
    }
};

export const getApplicantGuids = data => {
    return data.taxHousehold.householdMember.map(hhm => ({
        applicantGuid: hhm.applicantGuid
    }));
};

const getSSNs = data => {
    return data.taxHousehold.householdMember.map(hhm => ({
        applicantGuid: hhm.applicantGuid,
        ssn: hhm.socialSecurityCard.socialSecurityNumber
    }));
};

const getIMIdata = data => {
    return data.taxHousehold.householdMember.map(hhm => {
        const {
            applicantGuid,
            citizenshipImmigrationStatus: {
                citizenshipDocument: { alienNumber, foreignPassportOrDocumentNumber, i94Number, sevisId, visaNumber } = {}
            } = {}
        } = hhm ?? {};

        return ({ applicantGuid, alienNumber, foreignPassportOrDocumentNumber, i94Number, sevisId, visaNumber });
    });
};

export const maskPII = data => {
    const returnData = {
        ...data,
        taxHousehold: {
            ...data.taxHousehold,
            householdMember: data.taxHousehold.householdMember.map(hhm => ({
                ...hhm,
                socialSecurityCard: {
                    ...hhm.socialSecurityCard,
                    socialSecurityNumber: socialSecurityNumberTools.maskSsn(hhm.socialSecurityCard.socialSecurityNumber, true)
                },
                citizenshipImmigrationStatus: {
                    ...hhm.citizenshipImmigrationStatus,
                    citizenshipDocument: {
                        ...hhm.citizenshipImmigrationStatus.citizenshipDocument,
                        alienNumber: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.alienNumber),
                        foreignPassportOrDocumentNumber: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.foreignPassportOrDocumentNumber),
                        i94Number: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.i94Number),
                        sevisId: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.sevisId),
                        visaNumber: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.visaNumber)
                    }

                }
            }))
        }
    };
    return returnData;
};

export const getPayloadForSsnUpdate = (data, localState, pageName) => {
    if (pageName === PAGES.PERSONAL_INFORMATION_PAGE) {
        const [diff] = differenceWith(getSSNs(data), getSSNs(localState.frozenData), isEqual);
        return { ssnToUpdate: diff ? diff : null };
    } else {
        return { ssnToUpdate: null };
    }
};

export const getPayloadForIMIUpdate = (data, localState, pageName) => {
    if (pageName === PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE) {
        const [diff] = differenceWith(getIMIdata(data), getIMIdata(localState.frozenData), isEqual);
        return { immigrationInfoToUpdate: diff ? diff : null };
    } else {
        return { immigrationInfoToUpdate: null };
    }
};

export const mixValue = (values, value) => {
    if(value === null) return values;

    const { applicantGuid, ssn } = value;

    return {
        ...values,
        ...{ [ applicantGuid ]: ssn }
    };
};

// TODO: we enable getRequestBody function instead of getBody in Form component when the function is covered with unit tests
export const getRequestBody = params => {

    const {
        bloodRelationshipStructure,
        bypassedData,
        cachedData,
        data,
        dataSnapshot,
        dataToUpdate,
        destinationPage,
        impersonation,
        inlineCurrentIncomeEnabled,
        invokeInlineEligibility,
        isNonStandaloneInputChanged,
        isSogiFeatureEnabled,
        localState,
        maxAchievedPage,
        medicaidTerminationFeatureEnabled,
        page,
        pageName,
        serverDate,
        ssapApplicationId,
        taxHouseholdStructure
    } = params;

    const dataToSave = updateImiValues(
        updateSsnValues(
            bypassedData ? bypassedData : data,
            mixValue(cachedData.ssns, dataToUpdate.ssnToUpdate)
        ),
        cachedData.immigrationInfo
    );

    const resolvedData = updateDirtyFlags(
        dataToSave,
        dataSnapshot,
        cachedData
    );

    const areDiffsInHhms = !isEqual(
        getApplicantGuids(resolvedData),
        getApplicantGuids(localState.frozenData),
    );

    const {
        shouldRestrictNavigation,
        cleanedData
    } = performCleanUpAndChecks({
        dataAfterChanges: resolvedData,
        dataBeforeChanges: localState.frozenData,
        isSogiFeatureEnabled,
        medicaidTerminationFeatureEnabled,
        pageName,
        serverDate,
    });

    const inconsistencyCriteria = [
        areDiffsInHhms,
        isNonStandaloneInputChanged,
        shouldRestrictNavigation
    ];

    const isDataPotentiallyInconsistent = inconsistencyCriteria.some(criteria => criteria);

    return {
        application: JSON.stringify(
            processUiProperties({
                ...processJsonTools.convertTaxHousehold(
                    processJsonTools.convertBloodRelationships(
                        maskPII(cleanedData),
                        bloodRelationshipStructure
                    ),
                    taxHouseholdStructure
                ),
                ...getCurrentAndMaxAchievedPages(
                    destinationPage,
                    page,
                    maxAchievedPage,
                    isDataPotentiallyInconsistent,
                )
            })
        ),
        applicationId: ssapApplicationId,
        csrOverride: impersonation,
        recalculateIncome: !!bypassedData,
        invokeInlineEligibility: inlineCurrentIncomeEnabled ? invokeInlineEligibility : false,
        ...dataToUpdate
    };
};